import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, CheckCircle, Clock, TrendingUp, XCircle } from 'lucide-react';
import type { MonitoredSite, CheckResult } from '../../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SiteHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site: MonitoredSite | null;
  results: CheckResult[];
  uptimePercentage: number;
  avgResponseTime: number;
  loading: boolean;
}

export function SiteHistoryDialog({
  open,
  onOpenChange,
  site,
  results,
  uptimePercentage,
  avgResponseTime,
  loading
}: SiteHistoryDialogProps) {
  const statusColors = {
    HEALTHY: 'bg-green-500 text-white',
    WARNING: 'bg-yellow-500 text-white',
    CRITICAL: 'bg-red-500 text-white'
  };

  const statusIcons = {
    HEALTHY: <CheckCircle className="h-3 w-3" />,
    WARNING: <AlertCircle className="h-3 w-3" />,
    CRITICAL: <XCircle className="h-3 w-3" />
  };

  const statusLabels = {
    HEALTHY: 'OK',
    WARNING: 'Aviso',
    CRITICAL: 'Erro'
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Histórico de Verificações</DialogTitle>
          <DialogDescription>
            {site?.name} - Últimas 24 horas ou últimos 100 resultados
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-muted/50 rounded-lg p-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <TrendingUp className="h-4 w-4" />
                  Uptime
                </div>
                <div className="text-2xl font-bold">
                  {uptimePercentage.toFixed(2)}%
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Tempo Médio
                </div>
                <div className="text-2xl font-bold">
                  {avgResponseTime > 0 ? `${avgResponseTime.toFixed(0)}ms` : 'N/A'}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="h-4 w-4" />
                  Total de Checks
                </div>
                <div className="text-2xl font-bold">
                  {results.length}
                </div>
              </div>
            </div>

            {/* Results Table */}
            {results.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                Nenhum resultado encontrado
              </div>
            ) : (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>HTTP</TableHead>
                      <TableHead>Resposta</TableHead>
                      <TableHead>SSL</TableHead>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Problemas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={`${statusColors[result.status]} border-none flex items-center gap-1 w-fit`}
                          >
                            {statusIcons[result.status]}
                            {statusLabels[result.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {result.httpStatus || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {result.responseTimeMs ? `${result.responseTimeMs}ms` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {result.sslValid === true && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          {result.sslValid === false && (
                            <XCircle className="h-4 w-4 text-red-500" />
                          )}
                          {result.sslValid === undefined && (
                            <span className="text-muted-foreground text-xs">N/A</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {formatTimestamp(result.checkedAt)}
                        </TableCell>
                        <TableCell>
                          {result.issues && result.issues.length > 0 ? (
                            <div className="max-w-xs">
                              <details className="text-xs">
                                <summary className="cursor-pointer text-red-600 dark:text-red-400">
                                  {result.issues.length} problema(s)
                                </summary>
                                <ul className="mt-1 space-y-1 pl-4">
                                  {result.issues.map((issue, idx) => (
                                    <li key={idx}>{issue}</li>
                                  ))}
                                </ul>
                              </details>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
